import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi as defaultAdminApi } from './admin-api';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import './admin.css';

export function KiosksPage({ api = defaultAdminApi }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', kiosks: [], message: '' });
  const [label, setLabel] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const dialogRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!confirm) return undefined;
    const opener = returnFocusRef.current;
    const focusableSelector = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    confirmButtonRef.current?.focus();

    function trapTab(event) {
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(focusableSelector));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focusOutsideDialog = !dialogRef.current.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || focusOutsideDialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || focusOutsideDialog)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', trapTab);
    return () => {
      document.removeEventListener('keydown', trapTab);
      if (opener && document.body.contains(opener)) opener.focus();
      returnFocusRef.current = null;
    };
  }, [confirm]);

  const load = useCallback(async (activeRef) => {
    setState((previous) => ({ ...previous, status: 'loading', message: '' }));
    try {
      const { kiosks } = await api.listKiosks();
      if (!activeRef || activeRef.current) setState({ status: 'ready', kiosks: kiosks || [], message: '' });
    } catch (error) {
      if (activeRef && !activeRef.current) return;
      const outcome = handleApiError(error);
      if (!outcome.handled) setState({ status: 'error', kiosks: [], message: errorMessageFor(error) });
    }
  }, [api, handleApiError]);

  useEffect(() => {
    const active = { current: true };
    load(active);
    return () => { active.current = false; };
  }, [load]);

  async function create() {
    if (busy) return;
    setBusy(true); setActionError('');
    try {
      const result = await api.createKiosk(label.trim());
      setNotice(result.credentials);
      setLabel(''); setShowForm(false);
      setState((previous) => ({ ...previous, kiosks: [result.kiosk, ...previous.kiosks] }));
    } catch (error) {
      const outcome = handleApiError(error);
      if (!outcome.handled) setActionError(errorMessageFor(error));
    } finally { setBusy(false); }
  }

  async function confirmAction() {
    if (!confirm || busy) return;
    setBusy(true); setActionError('');
    try {
      if (confirm.type === 'rotate') {
        const result = await api.rotateKioskCredentials(confirm.kiosk.id);
        setNotice(result.credentials);
      } else {
        await api.updateKiosk(confirm.kiosk.id, { isActive: confirm.type === 'activate' });
      }
      setConfirm(null);
      await load();
    } catch (error) {
      const outcome = handleApiError(error);
      if (!outcome.handled) setActionError(errorMessageFor(error));
    } finally { setBusy(false); }
  }

  function openConfirm(action) {
    returnFocusRef.current = document.activeElement;
    setActionError('');
    setConfirm(action);
  }

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-admin-kiosks__header">
        <div><h1 id="gym-page-title" className="gym-page__title">Kioscos</h1><p className="gym-admin-kiosks__intro">Administrá las tablets de asistencia del gimnasio.</p></div>
        <div className="gym-admin-form-actions">
          <Link className="gym-button gym-button--secondary" to="/kiosco">Ver pantalla de ingreso</Link>
          {state.status === 'ready' && !showForm && !notice && <button type="button" className="gym-button gym-button--primary" onClick={() => setShowForm(true)}>Nueva tablet</button>}
        </div>
      </header>
      {notice && <>
        <div className="gym-admin-kiosks__backdrop" aria-hidden="true" />
        <div className="gym-admin-kiosks__notice gym-admin-sheet" role="status">
          <span className="gym-admin-kiosks__sheet-handle" aria-hidden="true" />
          <h2>Tablet creada</h2>
          <p className="gym-admin-kiosks__warning"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 3 2.8 19a1.5 1.5 0 0 0 1.3 2.2h15.8a1.5 1.5 0 0 0 1.3-2.2L12 3Z" /><path d="M12 9v5m0 3h.01" /></svg>Guardá estas credenciales ahora. La contraseña no se volverá a mostrar.</p>
          <dl><div className="gym-admin-kiosks__credential"><dt>Usuario</dt><dd><code>{notice.login}</code></dd></div><div className="gym-admin-kiosks__credential"><dt>Contraseña temporal</dt><dd><code>{notice.temporaryPassword}</code></dd></div></dl>
          <button type="button" className="gym-button gym-button--secondary" onClick={() => setNotice(null)}>Cerrar aviso</button>
        </div>
      </>}
      {showForm && <form className="gym-card gym-admin-kiosks__form" onSubmit={(event) => { event.preventDefault(); create(); }}>
        <label className="gym-field"><span>Nombre de la tablet</span><input className="gym-input" autoFocus maxLength="80" required value={label} onChange={(event) => setLabel(event.target.value)} /></label>
        {actionError && <p role="alert" className="gym-form__error">{actionError}</p>}
        <div className="gym-admin-form-actions"><button type="submit" className="gym-button gym-button--primary" disabled={busy || !label.trim()}>{busy ? 'Creando…' : 'Crear tablet'}</button><button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={() => { setShowForm(false); setActionError(''); }}>Cancelar</button></div>
      </form>}
      {state.status === 'loading' && <div role="status" className="gym-state gym-state--loading"><p className="gym-state__text">Cargando kioscos…</p></div>}
      {state.status === 'error' && <div role="alert" className="gym-state gym-state--error"><p className="gym-state__text">{state.message}</p><button type="button" className="gym-button gym-button--secondary" onClick={() => load()}>Reintentar</button></div>}
      {state.status === 'ready' && !state.kiosks.length && <div className="gym-state gym-state--empty"><p className="gym-state__text">Todavía no hay tablets cargadas.</p></div>}
      {state.status === 'ready' && state.kiosks.length > 0 && <div className="gym-admin-kiosks__list">
        {state.kiosks.map((kiosk) => <article className="gym-card gym-admin-kiosk-card" key={kiosk.id}>
          <div><h2>{kiosk.label}</h2><span className={kiosk.isActive ? 'gym-badge gym-badge--success' : 'gym-badge gym-badge--neutral'}>{kiosk.isActive ? 'Activa' : 'Inactiva'}</span></div>
          <div className="gym-admin-kiosk-card__actions">
            <button type="button" className="gym-button gym-button--danger-outline" aria-label={`Rotar credenciales de ${kiosk.label}`} disabled={busy} onClick={() => openConfirm({ type: 'rotate', kiosk })}>Rotar credenciales</button>
            <button type="button" className={`gym-button ${kiosk.isActive ? 'gym-button--danger-outline' : 'gym-button--secondary'}`} aria-label={`${kiosk.isActive ? 'Desactivar' : 'Activar'} tablet ${kiosk.label}`} disabled={busy} onClick={() => openConfirm({ type: kiosk.isActive ? 'deactivate' : 'activate', kiosk })}>{kiosk.isActive ? 'Desactivar' : 'Activar'}</button>
          </div>
        </article>)}
      </div>}
      {confirm && <div className="gym-plans-confirm-backdrop"><section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="gym-kiosk-confirm-title" className="gym-card gym-plans-confirm">
        <h2 id="gym-kiosk-confirm-title">{confirm.type === 'rotate' ? `¿Rotar credenciales de ${confirm.kiosk.label}?` : `¿${confirm.type === 'activate' ? 'Activar' : 'Desactivar'} ${confirm.kiosk.label}?`}</h2>
        <p>{confirm.type === 'rotate' ? 'La sesión actual de esta tablet se cerrará y la contraseña anterior dejará de funcionar.' : confirm.type === 'activate' ? 'La tablet podrá volver a iniciar sesión y registrar asistencias.' : 'La sesión actual se cerrará y esta tablet no podrá registrar asistencias.'}</p>
        {actionError && <p role="alert" className="gym-form__error">{actionError}</p>}
        <div className="gym-admin-form-actions"><button ref={confirmButtonRef} type="button" className={`gym-button ${confirm.type === 'activate' ? 'gym-button--secondary' : 'gym-button--danger'}`} disabled={busy} onClick={confirmAction}>{busy ? 'Procesando…' : `Confirmar ${confirm.type === 'rotate' ? 'rotación' : confirm.type === 'activate' ? 'activación' : 'desactivación'}`}</button><button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={() => setConfirm(null)}>Cancelar</button></div>
      </section></div>}
    </section>
  );
}
